import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row,
  Col,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import notify, { MESSAGES } from '../../../utils/notify';
import classnames from 'classnames';
import { getAuthToken, isAuthenticated } from '../../../auth/tokenvalidation';
import ActionButton from '../../../Components/Common/ActionButton';
import { EmptyState } from '../../../Components/Common/ListStates';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListFilterSelect,
  ListSearch,
} from '../../../Components/Common/ListFilters';
import { fetchJson } from '../../Procurement/utils/procurementList';
import { ProductAvatar } from '../../Procurement/component/ListBadges';
import '../../TenderOpening/components/TenderOpening.css';
import '../../Procurement/ProcurementView.css';
import '../Awarding.css';
import useAwardingList, {
  buildAwardingListUrl,
} from '../hooks/useAwardingList';
import { unwrapAwardingList } from '../utils/awardingHelpers';
import {
  AWARDING_STATUSES,
  exportAwardingList,
  getAwardingTone,
} from '../utils/awardingList';
import StatusBadge from '../../../Components/Common/StatusBadge';

// `status` is the Status query value ('' = no status filter).
const STAT_CARDS = [
  {
    key: 'total',
    status: '',
    label: 'Total Awarding',
    icon: 'ri-stack-line',
    tone: 'total',
  },
  ...AWARDING_STATUSES.map(status => ({
    key: status.value,
    status: status.value,
    label: status.label,
    icon: status.icon,
    tone: status.stat,
  })),
];

const SORT_FIELD_TO_COLUMN = {
  procurementNumber: 'noProc',
  titleProcurement: 'title',
  procurementType: 'procType',
};

const getStatusLabel = status => {
  const key = String(status ?? '')
    .replace(/\s+/g, '')
    .toLowerCase();
  return key === 'inprogress' ? 'In Progress' : status || '-';
};

const StatusPill = ({ status }) => {
  if (!status) return <span className='text-muted'>-</span>;

  const label = getStatusLabel(status);
  return (
    <StatusBadge tone={getAwardingTone(status)} label={label} title={label} />
  );
};

const UnreadBadge = ({ count }) =>
  count > 0 ? (
    <span
      className='badge bg-danger rounded-pill fs-11 ms-2'
      title={`${count} new response`}
    >
      {count}
    </span>
  ) : null;

const ProductInfo = ({ item }) => {
  const name = item?.productType?.main;
  const sub =
    item?.productType?.sub && item.productType.sub !== '-'
      ? item.productType.sub
      : '';
  if (!name && !sub) return null;

  return (
    <div className='to-product'>
      <span className='to-product-name'>{name || '-'}</span>
      {sub && <span className='text-truncate ms-2'>· {sub}</span>}
    </div>
  );
};

const StatCard = ({ card, summary, isActive, onSelect }) => {
  const total = summary?.total || 0;
  const count = summary?.[card.key];
  const share =
    card.key !== 'total' && total > 0 && typeof count === 'number'
      ? Math.round((count / total) * 100)
      : null;

  return (
    <button
      type='button'
      className={classnames('to-stat', `to-stat--${card.tone}`, {
        'is-active': isActive,
      })}
      aria-pressed={isActive}
      onClick={onSelect}
    >
      <span className='to-stat-icon'>
        <i className={card.icon} aria-hidden='true' />
      </span>
      <span>
        <span className='to-stat-label'>{card.label}</span>
        <span className='to-stat-value'>
          {typeof count === 'number' ? count : '–'}
        </span>
      </span>
      {share !== null && <span className='to-stat-share'>{share}%</span>}
    </button>
  );
};

const SkeletonRows = ({ count = 5 }) => (
  <div className='w-100 px-3 py-2'>
    {Array.from({ length: count }).map((_, index) => (
      <div key={index} className='d-flex align-items-center gap-3 py-3'>
        <div className='to-skeleton' style={{ width: '9%' }} />
        <div className='to-skeleton' style={{ width: '26%' }} />
        <div className='to-skeleton' style={{ width: '12%' }} />
        <div className='to-skeleton' style={{ width: '18%' }} />
        <div className='to-skeleton flex-grow-1' />
      </div>
    ))}
  </div>
);

const AwardingTable = ({
  procurementTypeHeader,
  refreshTrigger = 0,
  endpoint = '/awarding',
  detailBasePath = '/apps-awarding/dtl',
  onCreate,
  exportName = 'List',
}) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const {
    data,
    totalRows,
    totalPages,
    currentPage,
    rowsPerPage,
    searchText,
    debouncedSearchText,
    statusFilter,
    sortField,
    sortDirection,
    isLoading,
    startEntry,
    endEntry,
    handleSearch,
    handleStatusFilter,
    handlePageChange,
    handlePerRowsChange,
    handleSort,
    clearFilters,
  } = useAwardingList({ procurementTypeHeader, refreshTrigger, endpoint });

  // Counts come from the list endpoint with PageSize=1, reading totalItems.
  useEffect(() => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) return undefined;

    const controller = new AbortController();

    Promise.all(
      STAT_CARDS.map(async card => {
        const result = await fetchJson(
          buildAwardingListUrl({
            endpoint,
            currentPage: 1,
            rowsPerPage: 1,
            procurementTypeHeader,
            status: card.status,
          }),
          token,
          controller.signal
        );
        return [card.key, unwrapAwardingList(result).totalItems];
      })
    )
      .then(entries => setSummary(Object.fromEntries(entries)))
      .catch(error => {
        // The list request already reports load failures; cards just hide counts.
        if (error.name !== 'AbortError') setSummary(null);
      });

    return () => controller.abort();
  }, [endpoint, procurementTypeHeader, refreshTrigger]);

  // Exports every row matching the current filters, not just this page.
  const handleExport = async format => {
    const token = getAuthToken();
    if (!token || !isAuthenticated()) {
      notify.error(MESSAGES.authRequired);
      return;
    }

    setIsExporting(true);
    try {
      const query = {
        endpoint,
        currentPage: 1,
        procurementTypeHeader,
        search: debouncedSearchText,
        status: statusFilter,
        sortField,
        sortDirection,
      };
      let result = unwrapAwardingList(
        await fetchJson(
          buildAwardingListUrl({
            ...query,
            rowsPerPage: Math.max(totalRows, 1),
          }),
          token
        )
      );
      if (result.totalItems > result.items.length) {
        result = unwrapAwardingList(
          await fetchJson(
            buildAwardingListUrl({
              ...query,
              rowsPerPage: result.totalItems,
            }),
            token
          )
        );
      }

      if (!result.items.length) {
        notify.info(MESSAGES.noDataToExport);
        return;
      }
      exportAwardingList(result.items, { format, sheetName: exportName });
    } catch (error) {
      notify.apiError(error, 'export awarding data');
    } finally {
      setIsExporting(false);
    }
  };

  const items = data.items || [];
  const filterChips = [
    searchText && {
      key: 'search',
      label: 'Search',
      value: searchText,
      onRemove: () => handleSearch({ target: { value: '' } }),
    },
    statusFilter && {
      key: 'status',
      label: 'Status',
      value:
        AWARDING_STATUSES.find(option => option.value === statusFilter)
          ?.label ?? statusFilter,
      onRemove: () => handleStatusFilter(''),
    },
  ].filter(Boolean);
  const hasFilters = filterChips.length > 0;
  const goToDetail = row => navigate(`${detailBasePath}/${row.id}`);

  const emptyState = (
    <EmptyState
      title='No awarding found'
      emptyHint={
        onCreate
          ? 'No awardings have been created yet. Create one per procurement to start awarding.'
          : 'Awardings issued to you will appear here.'
      }
      filteredHint='Nothing matches the current search or status filter.'
      hasFilters={hasFilters}
      onClear={clearFilters}
      action={
        onCreate ? { label: 'Create Awarding', onClick: onCreate } : undefined
      }
    />
  );

  const columns = [
    {
      id: 'noProc',
      name: 'No Proc',
      selector: row => row.noProc,
      cell: row => (
        <span className='d-inline-flex align-items-center'>
          <span className='to-id'>{row.noProc || '-'}</span>
          <UnreadBadge count={row.unreadResponseCount} />
        </span>
      ),
      width: '12%',
      sortable: true,
    },
    {
      id: 'title',
      name: 'Procurement',
      selector: row => row.title,
      cell: row => (
        <div className='d-flex align-items-center gap-2 py-2 w-100'>
          <ProductAvatar name={row.productType?.main} />
          <div className='pv-text-col flex-grow-1'>
            <div className='to-title' title={row.title}>
              {row.title || '-'}
            </div>
            <ProductInfo item={row} />
          </div>
        </div>
      ),
      width: '30%',
      sortable: true,
    },
    {
      id: 'procType',
      name: 'Proc Type',
      selector: row => row.procType,
      cell: row => <span className='to-tag'>{row.procType || '-'}</span>,
      width: '15%',
      sortable: true,
    },
    {
      id: 'description',
      name: 'Description',
      selector: row => row.description,
      cell: row => (
        <span className='pv-desc' title={row.description || ''}>
          {row.description || '-'}
        </span>
      ),
      width: '20%',
      sortable: true,
    },
    {
      id: 'status',
      name: 'Status',
      selector: row => row.status,
      cell: row => <StatusPill status={row.status} />,
      width: '13%',
      sortable: true,
    },
    {
      name: <span className='text-center w-100'>Action</span>,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-arrow-right-up-line'
            label={row.noProc ? `View Detail ${row.noProc}` : 'View Detail'}
            onClick={e => {
              e.stopPropagation();
              goToDetail(row);
            }}
          />
        </div>
      ),
      width: '10%',
      center: true,
    },
  ];

  const MobileCard = ({ item }) => (
    <div
      className={classnames(
        'to-mcard d-md-none',
        `to-mcard--${getAwardingTone(item.status)}`
      )}
    >
      <div className='d-flex justify-content-between align-items-center gap-2 mb-2'>
        <span className='d-inline-flex align-items-center'>
          <span className='to-id'>{item.noProc || '-'}</span>
          <UnreadBadge count={item.unreadResponseCount} />
        </span>
        <StatusPill status={item.status} />
      </div>

      <div className='d-flex align-items-center gap-2'>
        <ProductAvatar name={item.productType?.main} />
        <div className='pv-text-col flex-grow-1'>
          <div className='to-title'>{item.title || '-'}</div>
          <ProductInfo item={item} />
        </div>
      </div>

      <div className='to-mcard-meta'>
        <div>
          <span>Proc Type</span>
          <span>{item.procType || '-'}</span>
        </div>
      </div>

      <div className='d-flex gap-2'>
        <ActionButton
          variant='secondary'
          size='sm'
          icon='ri-eye-line'
          className='flex-fill'
          onClick={() => goToDetail(item)}
        >
          View Detail
        </ActionButton>
      </div>
    </div>
  );

  return (
    <>
      <Row className='g-3 mb-3'>
        {STAT_CARDS.map(card => (
          <Col key={card.key} xs={12} sm={6} xl={3}>
            <StatCard
              card={card}
              summary={summary}
              isActive={statusFilter === card.status}
              onSelect={() =>
                handleStatusFilter(
                  statusFilter === card.status && card.status ? '' : card.status
                )
              }
            />
          </Col>
        ))}
      </Row>

      <div className='to-panel'>
        <div className='to-toolbar'>
          <ListSearch
            value={searchText}
            onChange={handleSearch}
            placeholder='Search by title or procurement no...'
            ariaLabel='Search awarding'
          />
          <ListFilterSelect
            label='Status'
            value={statusFilter}
            onChange={handleStatusFilter}
            allLabel='All Status'
            options={AWARDING_STATUSES}
          />
          <ClearFiltersButton
            count={filterChips.length}
            onClick={clearFilters}
          />
          <span className='to-toolbar-meta'>
            {isLoading
              ? 'Loading...'
              : `Showing ${startEntry}–${endEntry} of ${totalRows} awardings`}
          </span>
          <UncontrolledDropdown>
            <DropdownToggle
              tag='button'
              type='button'
              className='btn btn-light btn-sm d-inline-flex align-items-center'
              disabled={isExporting || isLoading || !totalRows}
            >
              {isExporting ? (
                <span
                  className='spinner-border spinner-border-sm me-2'
                  role='status'
                  aria-hidden='true'
                />
              ) : (
                <i className='ri-download-2-line me-2' aria-hidden='true' />
              )}
              Export
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => handleExport('xlsx')}>
                <i className='ri-file-excel-2-line align-middle me-2 text-success'></i>
                Export to Excel
              </DropdownItem>
              <DropdownItem onClick={() => handleExport('csv')}>
                <i className='ri-file-text-line align-middle me-2 text-muted'></i>
                Export to CSV
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>

        <ActiveFilterChips chips={filterChips} />

        <div className='d-md-none p-3'>
          {isLoading && <SkeletonRows count={3} />}
          {!isLoading &&
            items.map((item, index) => (
              <MobileCard key={item.id || index} item={item} />
            ))}
          {!isLoading && !items.length && emptyState}
        </div>

        <div className='d-none d-md-block to-table-wrap'>
          <DataTable
            key={`sort-${sortField}-${sortDirection}`}
            columns={columns}
            data={items}
            pagination={false}
            onSort={handleSort}
            sortServer
            defaultSortFieldId={SORT_FIELD_TO_COLUMN[sortField] || sortField}
            defaultSortAsc={sortDirection === 'asc'}
            onRowClicked={goToDetail}
            highlightOnHover
            pointerOnHover
            persistTableHead
            progressPending={isLoading}
            progressComponent={<SkeletonRows />}
            noDataComponent={emptyState}
            customStyles={{
              headCells: {
                style: { paddingLeft: '12px', paddingRight: '12px' },
              },
              cells: {
                style: {
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  wordBreak: 'break-word',
                },
              },
              table: { style: { width: '100%' } },
              tableWrapper: { style: { display: 'table' } },
            }}
          />
        </div>

        <div className='to-footer'>
          <div className='d-flex align-items-center gap-2'>
            <span>Rows per page</span>
            <select
              className='form-select form-select-sm'
              style={{ width: 'auto' }}
              value={rowsPerPage}
              onChange={e => handlePerRowsChange(parseInt(e.target.value, 10))}
              aria-label='Rows per page'
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className='to-footer-range'>
              {startEntry}–{endEntry} of <strong>{totalRows}</strong> items
            </span>
          </div>

          <div className='to-pager'>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(1)}
              aria-label='First page'
              title='First page'
            >
              <i className='ri-skip-back-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage <= 1 || isLoading}
              onClick={() => handlePageChange(currentPage - 1)}
              aria-label='Previous page'
              title='Previous page'
            >
              <i className='ri-arrow-left-s-line' aria-hidden='true' />
            </button>
            <span className='to-pager-current'>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(currentPage + 1)}
              aria-label='Next page'
              title='Next page'
            >
              <i className='ri-arrow-right-s-line' aria-hidden='true' />
            </button>
            <button
              type='button'
              className='to-pager-btn'
              disabled={currentPage >= totalPages || isLoading}
              onClick={() => handlePageChange(totalPages)}
              aria-label='Last page'
              title='Last page'
            >
              <i className='ri-skip-forward-line' aria-hidden='true' />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default React.memo(AwardingTable);
